import type { ClinSparkStudyPackage, StudyDetail, TabKey } from '../types'

interface Props {
  study: StudyDetail
  onNavigate: (tab: TabKey) => void
  parserMetadata?: Record<string, unknown>
  parserData?: ClinSparkStudyPackage
  /** Number of active protocol documents; the card is hidden when unknown. */
  documentCount?: number
}

export default function StudyOverviewTab({ study, onNavigate, parserMetadata, parserData, documentCount }: Props) {
  const metadataValue = (key: string, fallback?: string | null) => {
    const value = parserMetadata?.[key]
    return typeof value === 'string' && value && value !== 'NOT SPECIFIED' ? value : (fallback || '—')
  }
  const cmm = (parserData?.study_build_design?.CMM || {}) as Record<string, unknown>
  const arrayLength = (value: unknown, fallback = 0) => Array.isArray(value) ? value.length : fallback
  const count = (key: string, fallback: number) => arrayLength(cmm[key], fallback)
  const parserEligibility = parserData?.eligibility || { inclusion: [], exclusion: [] }
  const summaryItems = [
    { label: 'Visits', count: arrayLength(parserData?.visits, arrayLength(study.visits)), key: 'visits' as TabKey },
    { label: 'Forms', count: parserData ? count('form_dictionary', arrayLength(parserData.forms, arrayLength(study.forms))) : arrayLength(study.forms), key: 'visits' as TabKey },
    { label: 'Procedures', count: parserData ? arrayLength(parserData.procedures) : 0, key: 'visits' as TabKey },
    { label: 'Ranges', count: parserData ? count('range_dictionary', arrayLength(parserData.ranges)) : arrayLength(study.ranges), key: 'ranges' as TabKey },
    { label: 'Lab Panels', count: parserData ? count('laboratory_dictionary', arrayLength(parserData.lab_panels, arrayLength(study.lab_panels))) : arrayLength(study.lab_panels), key: 'lab-panels' as TabKey },
    { label: 'I/E Criteria', count: parserData ? arrayLength(parserEligibility.inclusion) + arrayLength(parserEligibility.exclusion) : arrayLength(study.criteria), key: 'ie' as TabKey },
    ...(documentCount === undefined ? [] : [{ label: documentCount === 1 ? 'Document' : 'Documents', count: documentCount, key: 'sources' as TabKey }]),
  ]

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</label>
          <p className="text-sm text-gray-900 dark:text-white">{metadataValue('protocol_title', study.study_name)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Code</label>
          <p className="text-sm text-gray-900 dark:text-white">{metadataValue('protocol_number', study.study_code)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sponsor</label>
          <p className="text-sm text-gray-900 dark:text-white">{metadataValue('sponsor', study.sponsor)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Phase</label>
          <p className="text-sm text-gray-900 dark:text-white">{metadataValue('phase', study.phase)}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Therapeutic Area</label>
          <p className="text-sm text-gray-900 dark:text-white">{metadataValue('therapeutic_area', study.therapeutic_area)}</p>
        </div>
      </div>
      {study.description && (
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
          <p className="text-sm text-gray-900 dark:text-white mt-1">{study.description}</p>
        </div>
      )}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Entity Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summaryItems.map((s) => (
            <button
              type="button"
              key={s.label}
              onClick={() => onNavigate(s.key)}
              className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="text-xl font-bold text-primary-600 dark:text-primary-400">{s.count}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{s.label}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
